import { useEffect, useRef } from 'react';
import type { Design } from '@/api/designs';
import { useAuthStore } from '@/stores';
import { getSessionStart } from '@/lib/session-start';

/** Keys already reported, kept across remounts of the overview. */
const REPORTED_STORAGE_KEY = 'designdeck:products-published:reported';

function readReported(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(REPORTED_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeReported(keys: Set<string>): void {
  try {
    window.sessionStorage.setItem(
      REPORTED_STORAGE_KEY,
      JSON.stringify([...keys])
    );
  } catch {
    // Storage may be unavailable in the embed; the in-memory set still
    // stops repeats within this mount.
  }
}

/**
 * Tells the embedding shop about products a marketplace job just created
 * (plan U10, KTD8).
 *
 * The publish step used to send this straight after its synchronous call. With
 * background jobs there is no result to report at that moment, so the overview
 * watches the channel state instead and posts once a marketplace publication
 * turns published while the seller is still inside the iframe.
 *
 * Leaving the iframe before the job finishes means no message. Only the shop's
 * product list is affected, and it refreshes on its own next load.
 *
 * The reported keys live in sessionStorage, not just in a ref: the seller
 * walks creator → overview → creator → overview, and each remount would
 * otherwise re-post every publication that is still marked published. A new
 * tab starts with an empty store, so publications that finished before this
 * tab's session began are treated as history and never posted.
 */
export function useNotifyShopOnPublish(
  designs: Design[],
  marketplaceChannelIds: string[]
): void {
  const isIframe = useAuthStore((state) => state.isIframe);
  // Ids already reported, so a refetch does not post the same success twice.
  const reported = useRef<Set<string> | null>(null);
  if (reported.current === null) reported.current = readReported();

  useEffect(() => {
    if (!isIframe || marketplaceChannelIds.length === 0) return;

    const freshlyPublished: string[] = [];
    const seen = reported.current as Set<string>;
    const sessionStart = getSessionStart();

    for (const design of designs) {
      for (const publication of design.channelPublications ?? []) {
        if (!marketplaceChannelIds.includes(publication.channelId)) continue;
        if (publication.status !== 'PUBLISHED') continue;

        const key = `${design.id}:${publication.channelId}`;
        if (seen.has(key)) continue;
        seen.add(key);

        // Finished before this tab existed: history, not news for the shop.
        const finishedAt = publication.finishedAt
          ? Date.parse(publication.finishedAt)
          : NaN;
        if (
          sessionStart !== null &&
          Number.isFinite(finishedAt) &&
          finishedAt < sessionStart
        ) {
          continue;
        }

        for (const item of publication.items) {
          if (item.targetId) freshlyPublished.push(item.targetId);
        }
      }
    }

    // Remember history as well, so the check above runs once per key.
    writeReported(seen);
    if (freshlyPublished.length === 0) return;

    // No wildcard target: the message carries the seller's product ids, and
    // "*" would hand them to whatever page happens to embed us. Without a
    // readable referrer we stay silent — the shop's product list refreshes on
    // its own next load anyway.
    let parentOrigin: string;
    try {
      if (!document.referrer) return;
      parentOrigin = new URL(document.referrer).origin;
    } catch {
      return;
    }

    window.parent.postMessage(
      { type: 'designdeck:products-published', productIds: freshlyPublished },
      parentOrigin
    );
  }, [designs, isIframe, marketplaceChannelIds]);
}
